import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import ScrollAnimation from '../../components/base/ScrollAnimation';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';
import { useSEO, generateServiceSchema, generateBreadcrumbSchema, generateFAQSchema } from '../../utils/seo';

export default function VehicleDecalsPage() {
  const faqs = [
    {
      question: 'How long do vehicle decals last?',
      answer: 'High-quality vinyl decals typically last 5-7 years with proper care. Factors like sun exposure, washing frequency, and maintenance affect longevity. We use premium materials for maximum durability.',
    },
    {
      question: 'Will decals damage my vehicle paint?',
      answer: 'No! Professional vinyl actually protects your paint from scratches and UV damage. When removed properly by professionals, it leaves no residue or damage. We also offer window tinting for additional paint and interior protection.',
    },
    {
      question: 'Can I design my own graphics?',
      answer: 'Absolutely! Bring your design ideas and we\'ll create custom mockups. We also offer professional design services if you need help bringing your vision to life. Free design consultation included.',
    },
    {
      question: 'Do you offer installation at my location?',
      answer: 'Yes! We provide mobile installation services for your convenience. Perfect for fleet vehicles or if you prefer service at your home or business. Learn more about our mobile tinting and graphics services.',
    },
    {
      question: 'How much does vehicle wrapping cost?',
      answer: 'Costs vary based on vehicle size, design complexity, and coverage area. Partial wraps start around $500, while full wraps range from $2,500-$5,000. Contact us for a free quote tailored to your project.',
    },
    {
      question: 'Can you match my company branding colors?',
      answer: 'Yes! We can match any color from your brand guidelines. Our design team ensures your vehicle graphics perfectly represent your business identity and maintain brand consistency across your fleet.',
    },
    {
      question: 'Do you offer fleet graphics services?',
      answer: 'Yes! We specialize in fleet branding with volume discounts for multiple vehicles. We ensure consistent branding across your entire fleet and offer flexible scheduling. We also provide commercial window tinting for your business location.',
    },
    {
      question: 'Can decals be removed or updated?',
      answer: 'Yes! Vinyl graphics can be removed without damaging paint. This makes it easy to update your branding, change designs, or remove graphics when selling your vehicle. We offer professional removal services.',
    },
  ];

  useSEO({
    title: 'Vehicle Decals & Graphics Baltimore - Custom Vinyl Wraps | U.B.K',
    description: 'Custom vehicle decals and graphics in Baltimore, MD. Business branding, fleet graphics, and personal customization. Professional vinyl wrap installation. Free design consultation. Call (443) 800-0348.',
    keywords: 'vehicle decals Baltimore, vinyl graphics Maryland, vehicle wraps, fleet graphics, business branding, custom car graphics',
    canonical: '/vehicle-decals',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateServiceSchema(
          'Vehicle Decals & Graphics',
          'Professional vehicle decals and vinyl graphics. Custom designs for business branding, fleet services, and personal customization.',
          '/vehicle-decals'
        ),
        generateFAQSchema(faqs),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Vehicle Decals', url: '/vehicle-decals' },
        ]),
      ],
    },
  });

  const services = [
    {
      title: 'Business Branding',
      description: 'Transform your vehicle into a mobile billboard. Professional vinyl graphics for company branding and advertising.',
      icon: 'ri-building-line',
      image: 'https://public.readdy.ai/ai/img_res/3e8d7cda5e56ba39a7b0e16349515abe.jpg',
    },
    {
      title: 'Custom Graphics',
      description: 'Personalize your ride with custom designs, racing stripes, or unique artwork. Make your vehicle stand out.',
      icon: 'ri-palette-line',
      image: 'https://readdy.ai/api/search-image?query=custom-vehicle-graphics-and-racing-stripes-on-sports-car-vibrant-colors-professional-vinyl-wrap-installation-sleek-modern-design-automotive-customization&width=600&height=400&seq=custom1&orientation=landscape',
    },
    {
      title: 'Fleet Graphics',
      description: 'Consistent branding across your entire fleet. Volume pricing available for multiple vehicles.',
      icon: 'ri-truck-line',
      image: 'https://readdy.ai/api/search-image?query=fleet-of-branded-commercial-vehicles-with-matching-vinyl-graphics-professional-business-trucks-and-vans-corporate-branding-uniform-design-high-quality&width=600&height=400&seq=fleet2&orientation=landscape',
    },
    {
      title: 'Partial Wraps',
      description: 'Cost-effective solution with maximum impact. Strategic placement for visibility and brand recognition.',
      icon: 'ri-layout-line',
      image: 'https://readdy.ai/api/search-image?query=partial-vehicle-wrap-with-professional-graphics-modern-car-with-strategic-vinyl-decal-placement-clean-design-automotive-branding-high-quality-finish&width=600&height=400&seq=partial1&orientation=landscape',
    },
  ];

  const benefits = [
    {
      icon: 'ri-eye-line',
      title: 'Maximum Visibility',
      description: 'Your vehicle becomes a moving advertisement, reaching thousands of potential customers daily.',
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Cost Effective',
      description: 'Lower cost per impression than traditional advertising. One-time investment with long-term returns.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Paint Protection',
      description: 'Vinyl wraps protect your original paint from scratches, UV damage, and minor abrasions.',
    },
    {
      icon: 'ri-refresh-line',
      title: 'Easy Updates',
      description: 'Change your design anytime. Remove or update graphics without damaging paint.',
    },
    {
      icon: 'ri-award-line',
      title: 'Professional Image',
      description: 'Enhance your brand credibility with professional, eye-catching vehicle graphics.',
    },
    {
      icon: 'ri-time-line',
      title: 'Long Lasting',
      description: 'High-quality vinyl lasts 5-7 years with proper care. Excellent return on investment.',
    },
  ];

  const process = [
    {
      step: '1',
      title: 'Consultation',
      description: 'Discuss your vision, branding goals, and budget. We provide expert recommendations.',
      icon: 'ri-chat-check-line',
    },
    {
      step: '2',
      title: 'Design',
      description: 'Our designers create custom mockups. Revisions included until you love it.',
      icon: 'ri-pencil-ruler-line',
    },
    {
      step: '3',
      title: 'Production',
      description: 'High-quality vinyl printed with vibrant, fade-resistant inks.',
      icon: 'ri-printer-line',
    },
    {
      step: '4',
      title: 'Installation',
      description: 'Expert application ensures perfect alignment and bubble-free finish.',
      icon: 'ri-tools-line',
    },
  ];

  return (
    <div className="min-h-screen">
      <Navigation />
      <main className="pt-24 max-md:pt-20">
        {/* Hero Section */}
        <section className="relative pt-12 md:pt-16 pb-8 md:pb-12 bg-gradient-to-br from-secondary via-gray-800 to-secondary overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
              backgroundSize: '50px 50px',
            }}></div>
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12 items-center">
              <div className="text-white">
                <div className="inline-block bg-accent text-white px-3 md:px-4 py-1 md:py-2 rounded-full font-semibold mb-2 md:mb-4 text-xs md:text-base">
                  <i className="ri-palette-line mr-1 md:mr-2"></i>
                  Custom Design Available
                </div>
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-3 md:mb-6 leading-tight">
                  Vehicle Decals & Graphics
                </h1>
                <p className="text-sm md:text-xl text-gray-300 mb-4 md:mb-8 leading-relaxed">
                  Transform your vehicle into a powerful marketing tool. Professional vinyl graphics for business branding, personal customization, and fleet services.
                </p>
                <div className="flex flex-col sm:flex-row gap-2 md:gap-4">
                  <Link to="/contact">
                    <Button size="lg" className="text-xs md:text-base py-2 md:py-3 px-4 md:px-6">
                      <i className="ri-image-add-line mr-1 md:mr-2"></i>
                      Start Your Design
                    </Button>
                  </Link>
                  <a href="tel:4438000348">
                    <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-secondary text-xs md:text-base py-2 md:py-3 px-4 md:px-6">
                      <i className="ri-phone-line mr-1 md:mr-2"></i>
                      Discuss Your Project
                    </Button>
                  </a>
                </div>
              </div>
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=professional-vehicle-wrap-and-decal-installation-modern-car-with-custom-graphics-expert-technician-applying-vinyl-high-quality-automotive-customization-studio-setting&width=800&height=600&seq=decal1&orientation=landscape"
                  alt="Vehicle Decals and Graphics"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-8 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollAnimation animation="fade-in">
              <div className="text-center mb-8 md:mb-16">
                <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-secondary mb-2 md:mb-4">
                  Our Decal Services
                </h2>
                <p className="text-sm md:text-xl text-gray-600 max-w-3xl mx-auto">
                  From business branding to personal customization, we create stunning vehicle graphics that make an impact.
                </p>
              </div>
            </ScrollAnimation>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
              {services.map((service, index) => (
                <ScrollAnimation
                  key={service.title}
                  animation={index % 2 === 0 ? 'slide-in-left' : 'slide-in-right'}
                  delay={index * 100}
                >
                  <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                    <div className="relative h-40 md:h-64">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      <div className="absolute bottom-2 md:bottom-4 left-2 md:left-4 flex items-center space-x-2 md:space-x-3">
                        <div className="w-8 h-8 md:w-12 md:h-12 bg-primary rounded-full flex items-center justify-center">
                          <i className={`${service.icon} text-white text-base md:text-2xl`}></i>
                        </div>
                        <h3 className="text-base md:text-2xl font-bold text-white">{service.title}</h3>
                      </div>
                    </div>
                    <div className="p-3 md:p-6">
                      <p className="text-xs md:text-base text-gray-600 leading-relaxed">{service.description}</p>
                    </div>
                  </div>
                </ScrollAnimation>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-8 md:py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollAnimation animation="fade-in">
              <div className="text-center mb-8 md:mb-16">
                <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-secondary mb-2 md:mb-4">
                  Why Choose Vehicle Graphics?
                </h2>
                <p className="text-sm md:text-xl text-gray-600 max-w-3xl mx-auto">
                  Turn your vehicle into a powerful marketing asset with professional vinyl graphics.
                </p>
              </div>
            </ScrollAnimation>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
              {benefits.map((benefit, index) => (
                <ScrollAnimation key={benefit.title} animation="slide-up" delay={index * 100}>
                  <div className="bg-white rounded-xl p-4 md:p-6 border border-gray-200 hover:border-primary hover:shadow-xl transition-all duration-300">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mb-2 md:mb-4">
                      <i className={`${benefit.icon} text-white text-xl md:text-3xl`}></i>
                    </div>
                    <h3 className="text-base md:text-xl font-bold text-secondary mb-1.5 md:mb-3">{benefit.title}</h3>
                    <p className="text-xs md:text-base text-gray-600 leading-relaxed">{benefit.description}</p>
                  </div>
                </ScrollAnimation>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-8 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollAnimation animation="fade-in">
              <div className="text-center mb-8 md:mb-16">
                <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-secondary mb-2 md:mb-4">
                  Our Design & Installation Process
                </h2>
                <p className="text-sm md:text-xl text-gray-600 max-w-3xl mx-auto">
                  From concept to completion, we ensure your vision becomes reality.
                </p>
              </div>
            </ScrollAnimation>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
              {process.map((step, index) => (
                <ScrollAnimation key={step.step} animation="slide-up" delay={index * 100}>
                  <div className="text-center">
                    <div className="w-12 h-12 md:w-20 md:h-20 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mx-auto mb-2 md:mb-4">
                      <i className={`${step.icon} text-white text-xl md:text-3xl`}></i>
                    </div>
                    <div className="text-2xl md:text-4xl font-bold text-gray-200 mb-1 md:mb-2">{step.step}</div>
                    <h3 className="text-base md:text-xl font-bold text-secondary mb-1.5 md:mb-3">{step.title}</h3>
                    <p className="text-xs md:text-base text-gray-600 leading-relaxed">{step.description}</p>
                  </div>
                </ScrollAnimation>
              ))}
            </div>
          </div>
        </section>

        {/* Portfolio Section */}
        <section className="py-8 md:py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollAnimation animation="fade-in">
              <div className="text-center mb-8 md:mb-16">
                <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-secondary mb-2 md:mb-4">
                  Design Possibilities
                </h2>
                <p className="text-sm md:text-xl text-gray-600 max-w-3xl mx-auto">
                  From subtle accents to full wraps, we bring your vision to life with premium materials and expert installation.
                </p>
              </div>
            </ScrollAnimation>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6">
              {[
                'Company logos and branding',
                'Contact information displays',
                'Racing stripes and accents',
                'Custom artwork and designs',
                'Promotional graphics',
                'Fleet identification',
                'Window decals',
                'Hood and roof graphics',
                'Side panel designs',
              ].map((item, index) => (
                <ScrollAnimation key={item} animation="slide-up" delay={index * 50}>
                  <div className="bg-white rounded-lg p-3 md:p-6 border-l-4 border-primary hover:shadow-lg transition-all duration-300">
                    <div className="flex items-center space-x-2 md:space-x-3">
                      <i className="ri-check-double-line text-primary text-base md:text-2xl w-4 h-4 md:w-6 md:h-6 flex items-center justify-center"></i>
                      <span className="text-sm md:text-lg font-semibold text-secondary">{item}</span>
                    </div>
                  </div>
                </ScrollAnimation>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-8 md:py-24 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollAnimation animation="fade-in">
              <div className="text-center mb-8 md:mb-16">
                <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-secondary mb-2 md:mb-4">
                  Frequently Asked Questions
                </h2>
                <p className="text-sm md:text-xl text-gray-600">
                  Everything you need to know about vehicle decals and graphics
                </p>
              </div>
            </ScrollAnimation>

            <div className="space-y-2 md:space-y-4">
              {faqs.map((faq, index) => (
                <ScrollAnimation key={index} animation="slide-up" delay={index * 50}>
                  <details className="bg-white rounded-xl shadow-md overflow-hidden group">
                    <summary className="px-4 md:px-6 py-3 md:py-5 cursor-pointer list-none flex items-center justify-between hover:bg-gray-50 transition-colors">
                      <h3 className="text-sm md:text-lg font-semibold text-secondary pr-4">{faq.question}</h3>
                      <i className="ri-arrow-down-s-line text-xl md:text-2xl text-primary group-open:rotate-180 transition-transform w-5 h-5 md:w-6 md:h-6 flex items-center justify-center flex-shrink-0"></i>
                    </summary>
                    <div className="px-4 md:px-6 pb-3 md:pb-5 text-xs md:text-base text-gray-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                </ScrollAnimation>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-8 md:py-24 bg-gradient-to-br from-primary to-accent text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <ScrollAnimation animation="fade-in">
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-6">
                Ready to Make Your Vehicle Stand Out?
              </h2>
              <p className="text-sm md:text-xl mb-4 md:mb-8 opacity-90">
                Let's create stunning graphics that represent your brand and style. Free design consultation included!
              </p>
              <div className="flex flex-col sm:flex-row gap-2 md:gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" variant="secondary" className="min-w-[160px] md:min-w-[200px] text-xs md:text-base py-2 md:py-3">
                    <i className="ri-image-add-line mr-1 md:mr-2"></i>
                    Request Quote
                  </Button>
                </Link>
                <a href="tel:4438000348">
                  <Button size="lg" variant="outline" className="min-w-[160px] md:min-w-[200px] border-white text-white hover:bg-white hover:text-primary text-xs md:text-base py-2 md:py-3">
                    <i className="ri-phone-line mr-1 md:mr-2"></i>
                    (443) 800-0348
                  </Button>
                </a>
              </div>
            </ScrollAnimation>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
